import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, GitFork, Pencil } from "lucide-react";
import { INDUSTRY_TAGS } from "@/lib/types";
import type { Idea } from "@/lib/types";

interface IdeaFormProps {
	mode: "submit" | "fork" | "edit";
	existingIdea?: Idea;
	onSubmit: (data: {
		title: string;
		problem: string;
		solution: string;
		targetMarket: string;
		monetization: string;
		tags: string[];
		changeNote: string;
	}) => void;
	onBack: () => void;
}

function wordCount(text: string): number {
	if (!text.trim()) return 0;
	return text.trim().split(/\s+/).length;
}

const MAX_WORDS = 200;

function WordCounter({ text, label }: { text: string; label: string }) {
	const count = wordCount(text);
	const isOver = count > MAX_WORDS;
	return (
		<div
			className={`text-xs mt-1 text-right ${
				isOver ? "text-red-500 font-medium" : "text-muted-foreground"
			}`}
		>
			{count}/{MAX_WORDS} words
		</div>
	);
}

export function IdeaForm({
	mode,
	existingIdea,
	onSubmit,
	onBack,
}: IdeaFormProps) {
	const [title, setTitle] = useState(existingIdea?.title ?? "");
	const [problem, setProblem] = useState(existingIdea?.problem ?? "");
	const [solution, setSolution] = useState(existingIdea?.solution ?? "");
	const [targetMarket, setTargetMarket] = useState(
		existingIdea?.targetMarket ?? "",
	);
	const [monetization, setMonetization] = useState(
		existingIdea?.monetization ?? "",
	);
	const [tags, setTags] = useState<string[]>(existingIdea?.tags ?? []);
	const [changeNote, setChangeNote] = useState("");

	const totalWords = useMemo(
		() =>
			wordCount(problem) +
			wordCount(solution) +
			wordCount(targetMarket) +
			wordCount(monetization),
		[problem, solution, targetMarket, monetization],
	);

	const isValid =
		title.trim() &&
		problem.trim() &&
		solution.trim() &&
		targetMarket.trim() &&
		monetization.trim() &&
		tags.length > 0 &&
		(mode !== "edit" || changeNote.trim());

	const toggleTag = (tag: string) => {
		setTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
		);
	};

	const handleSubmit = () => {
		if (!isValid) return;
		onSubmit({
			title: title.trim(),
			problem: problem.trim(),
			solution: solution.trim(),
			targetMarket: targetMarket.trim(),
			monetization: monetization.trim(),
			tags,
			changeNote:
				mode === "fork"
					? "Forked and improved"
					: mode === "edit"
						? changeNote.trim()
						: "Initial submission",
		});
	};

	const modeConfig = {
		submit: {
			heading: "Submit a Startup Idea",
			subheading:
				"Share your idea with the community. Keep each section under 200 words.",
			icon: <Send className="w-5 h-5" />,
			buttonText: "Submit Idea",
		},
		fork: {
			heading: "Fork & Improve",
			subheading: `Improving upon: ${existingIdea?.title}. Make it better!`,
			icon: <GitFork className="w-5 h-5" />,
			buttonText: "Submit Fork",
		},
		edit: {
			heading: "Edit Idea",
			subheading:
				"Update your idea. Changes are tracked in version history.",
			icon: <Pencil className="w-5 h-5" />,
			buttonText: "Save Changes",
		},
	};

	const config = modeConfig[mode];

	return (
		<div className="space-y-6">
			<button
				onClick={onBack}
				className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
			>
				<ArrowLeft className="w-4 h-4" />
				Back
			</button>

			<div className="rounded-xl border border-border bg-card p-6 space-y-6">
				<div>
					<h2 className="text-xl font-bold flex items-center gap-2">
						{config.icon}
						{config.heading}
					</h2>
					<p className="text-sm text-muted-foreground mt-1">
						{config.subheading}
					</p>
				</div>

				{/* Title */}
				<div className="space-y-2">
					<Label htmlFor="title" className="text-sm font-medium">
						Idea Title
					</Label>
					<Input
						id="title"
						placeholder="e.g., MealMind - AI Meal Planning for Dietary Restrictions"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						className="h-11"
					/>
				</div>

				{/* Problem */}
				<div className="space-y-2">
					<Label htmlFor="problem" className="text-sm font-medium">
						Problem
					</Label>
					<Textarea
						id="problem"
						placeholder="What problem does this solve? Who is affected and how?"
						value={problem}
						onChange={(e) => setProblem(e.target.value)}
						className="min-h-[100px] resize-none"
					/>
					<WordCounter text={problem} label="Problem" />
				</div>

				{/* Solution */}
				<div className="space-y-2">
					<Label htmlFor="solution" className="text-sm font-medium">
						Solution
					</Label>
					<Textarea
						id="solution"
						placeholder="How does your product solve this problem? What makes it unique?"
						value={solution}
						onChange={(e) => setSolution(e.target.value)}
						className="min-h-[100px] resize-none"
					/>
					<WordCounter text={solution} label="Solution" />
				</div>

				{/* Target Market */}
				<div className="space-y-2">
					<Label htmlFor="market" className="text-sm font-medium">
						Target Market
					</Label>
					<Textarea
						id="market"
						placeholder="Who are your target customers? How big is the market?"
						value={targetMarket}
						onChange={(e) => setTargetMarket(e.target.value)}
						className="min-h-[100px] resize-none"
					/>
					<WordCounter text={targetMarket} label="Target Market" />
				</div>

				{/* Monetization */}
				<div className="space-y-2">
					<Label htmlFor="monetization" className="text-sm font-medium">
						Monetization
					</Label>
					<Textarea
						id="monetization"
						placeholder="How will you make money? Pricing model, revenue streams?"
						value={monetization}
						onChange={(e) => setMonetization(e.target.value)}
						className="min-h-[100px] resize-none"
					/>
					<WordCounter text={monetization} label="Monetization" />
				</div>

				{/* Tags */}
				<div className="space-y-2">
					<Label className="text-sm font-medium">
						Industry Tags{" "}
						<span className="text-muted-foreground font-normal">
							(select at least 1)
						</span>
					</Label>
					<div className="flex flex-wrap gap-1.5">
						{INDUSTRY_TAGS.map((tag) => (
							<Badge
								key={tag}
								variant={tags.includes(tag) ? "default" : "outline"}
								className={`cursor-pointer text-xs transition-colors ${
									tags.includes(tag)
										? "bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
										: "hover:border-orange-300 hover:text-orange-600"
								}`}
								onClick={() => toggleTag(tag)}
							>
								{tag}
							</Badge>
						))}
					</div>
				</div>

				{/* Change note (edit mode) */}
				{mode === "edit" && (
					<div className="space-y-2">
						<Label htmlFor="changeNote" className="text-sm font-medium">
							Change Note
						</Label>
						<Input
							id="changeNote"
							placeholder="What did you change? e.g., Updated pricing model"
							value={changeNote}
							onChange={(e) => setChangeNote(e.target.value)}
						/>
					</div>
				)}

				{/* Total word count + submit */}
				<div className="flex items-center justify-between pt-2">
					<div className="text-sm text-muted-foreground">
						Total:{" "}
						<span
							className={`font-medium ${totalWords > 800 ? "text-red-500" : "text-foreground"}`}
						>
							{totalWords}
						</span>{" "}
						words across all sections
					</div>
					<Button
						onClick={handleSubmit}
						disabled={!isValid}
						className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white gap-2"
					>
						{config.icon}
						{config.buttonText}
					</Button>
				</div>
			</div>
		</div>
	);
}
